'use client'

import React, { useEffect, useState } from 'react'
import { useRegionStore } from '../../../store/regionStore'
import { useRegionTurnkeyOptions } from '../../RegionSurfaces'
import styles from './styles.module.css'
import SimpleBlockContent from '../../SimpleBlockContent'
import CountriesAutocomplete from '../../MyAutocomplete/CountriesAutocomplete'
import Text from '../../Text'
import CheckmarkIcon from '../../icons/CheckmarkIcon'
import Spacer from '../../Spacer'
import { CircularProgress } from '@mui/material'
import { formatPrice, price } from '../../../utils/formats'
import ContactForm from './ContactForm'
import { PALETTE } from '../../../styles/constants'
import Gallery from './Gallery'
import { remove } from '../../../utils/arrays'
import Option from './Option'

function TurnkeySolutions({ backgroundColor, textColor, title, subtitle, features }) {
  // Runtime CMS-driven colours -> inline style (matches former styled `${props}`).
  const colorStyle = { background: backgroundColor.value, color: textColor.value }

  // The turnkey SURFACE (turnkeyOptions) comes from the SSR-threaded
  // <RegionSurfacesProvider> (Phase A2.2); the shipping-picker reads
  // (countries + shippingCountry) from the Zustand region store (Phase C2 part 2).
  const countries = useRegionStore((s) => s.countries)
  const shippingCountry = useRegionStore((s) => s.shippingCountry)
  const { turnkeyOptions, ensureRequested } = useRegionTurnkeyOptions()

  const [selectedOptions, setSelectedOptions] = useState({})
  const [showGalleryOptionId, setShowGalleryOptionId] = useState(null)

  const makeSelection = (option, category, isMultiSelectionAllowed) => {
    const selectedOptionsCopy = { ...selectedOptions }
    let optionsForCategory = selectedOptionsCopy[category] || []
    if (isMultiSelectionAllowed) {
      const optionAlreadySelected = optionsForCategory.includes(option.id)
      optionAlreadySelected ? remove(optionsForCategory, option.id) : optionsForCategory.push(option.id)
    } else {
      optionsForCategory = [option.id]
    }

    selectedOptionsCopy[category] = optionsForCategory

    setSelectedOptions(selectedOptionsCopy)
    if (category === turnkeyOptions.mainCategory) {
      setShowGalleryOptionId(option.id)
    }
  }

  useEffect(() => {
    ensureRequested()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // #418: gate the loader on THIS section's OWN data (turnkeyOptions), NOT the
  // global state.isLoading flag. turnkeyOptions is SSR-hydrated whenever the page
  // renders this section (buildSurfaceMutations), so it is PRESENT on the client's
  // first render — SSR and client-first render content identically. The loader
  // shows only when turnkeyOptions is genuinely absent (SAME on both sides). The
  // old `state.isLoading ||` term emitted a spinner into the SSR HTML whenever any
  // OTHER section's fetch flipped the global flag (the latency-only #418 cascade).
  if (!turnkeyOptions) {
    // Loading previously used backgroundColor + color; keep both inline.
    return (
      <div className={styles.loading} style={{ backgroundColor: backgroundColor.value, color: textColor.value }}>
        <CircularProgress color="inherit"/>
      </div>
    )
  }

  const renderOption = (option, category, isMultiSelectionAllowed) => {
    const isSelected = selectedOptions[category] ? selectedOptions[category].includes(option.id) : option.isDefault
    const onClick = () => makeSelection(option, category, isMultiSelectionAllowed)
    const onMouseEnter = () => category === turnkeyOptions.mainCategory && setShowGalleryOptionId(option.id)
    const onMouseLeave = () => category === turnkeyOptions.mainCategory && setShowGalleryOptionId(null)

    return <Option option={option}
                   isSelected={isSelected}
                   isMultiSelectionAllowed={isMultiSelectionAllowed}
                   onClick={onClick}
                   onMouseEnter={onMouseEnter}
                   onMouseLeave={onMouseLeave}/>
  }

  const getSelectedOptionsIds = () => {
    if (!turnkeyOptions.optionsByCategory) {
      return []
    }

    const selectedOptionIds = []
    const categories = Object.keys(turnkeyOptions.optionsByCategory)
    categories.forEach(c => {
      if (selectedOptions[c] && selectedOptions[c].length) {
        selectedOptionIds.push(...selectedOptions[c])
      } else {
        const defaultOption = turnkeyOptions.optionsByCategory[c].find(o => o.isDefault)
        defaultOption && selectedOptionIds.push(defaultOption.id)
      }
    })

    return selectedOptionIds
  }

  const getTotal = () => {
    const selectedIds = getSelectedOptionsIds()
    if (!selectedIds || !selectedIds.length) {
      return price(0, 'USD')
    }

    const allOptions = Object.values(turnkeyOptions.optionsByCategory).flat()

    const total = selectedIds
      .map(id => allOptions.find(o => o.id === id))
      .filter(o => o != null)
      .reduce((accumulator , option) => {
        return accumulator + option.price.priceCents
      }, 0)

    return price(total, allOptions[0].price.currency)
  }

  const turnkeyAvailable = () => {
    return !!turnkeyOptions.optionsByCategory
  }

  return (
    <div className={styles.wrapper} style={colorStyle}>
      <div className={styles.galleryWrapper}>
        {
          (() => {
            if (turnkeyOptions.optionsByCategory && turnkeyAvailable()) {
              return turnkeyOptions.optionsByCategory[turnkeyOptions.mainCategory]
                .map(o =>
                  <Gallery
                    key={o.id}
                    name={o.name}
                    images={o.images}
                    show={showGalleryOptionId ? showGalleryOptionId === o.id : getSelectedOptionsIds().includes(o.id)}
                  />
                )
            }
          }) ()
        }
      </div>
      <div className={styles.optionsWrapper}>
        <div className={styles.title}>
          {/* Single-<h1> rule (Audit A #1): /turnkey-sim configurator title is the
              page's one visible <h1>. `.h1-contents` adds no box. */}
          <h1 className="h1-contents"><SimpleBlockContent blocks={title} noSpace/></h1>
          <div className={styles.subtitle} style={{ display: turnkeyOptions.startingAtPrice ? 'block' : 'none' }}>
            <Text type="h4">Starting at {turnkeyOptions.startingAtPrice && formatPrice(turnkeyOptions.startingAtPrice)}</Text>
            <Text type="caption-1" color={PALETTE.gray50}>Excluding tax and shipping</Text>
          </div>
        </div>

        <div className={styles.separator} style={{ display: 'block' }}/>

        <div className={styles.autocompleteWrapper}>
          <Text type="body-short-2">Shipping to</Text>
          <CountriesAutocomplete
            countries={countries}
            selectedCountryCode={shippingCountry}
            inputTextColor={textColor.value}
            popupIndicatorColor={textColor.value}
            fontSizeAdjust={2}
          />
        </div>

        <div className={styles.separator} style={{ display: 'block' }}/>

        <div className={styles.featuresWrapper} style={{ display: turnkeyAvailable() ? 'flex' : 'none' }}>
          <Text type="h4">With all builds you will receive</Text>
          <Spacer size={16}/>
          {
            features.map(f => (
              <div className={styles.feature} key={f}>
                <CheckmarkIcon/>
                <Text type="caption-1">{f}</Text>
              </div>
            ))
          }
        </div>

        <div className={styles.separator} style={{ display: turnkeyAvailable() ? 'block' : 'none' }}/>

        {turnkeyAvailable() && <Text type="h3">Create your simulator</Text>}
        {!turnkeyAvailable() && <Text type="body-short-1">Sorry, our custom built simulators are currently not available for your location.</Text>}
        {
          turnkeyOptions.optionsByCategory && Object.keys(turnkeyOptions.optionsByCategory).map(c => {
            const options = turnkeyOptions.optionsByCategory[c]

            if (options.length) {
              const categoryInfo = turnkeyOptions.categoryToInfo[c]

              return (
                <div className={styles.optionsList} key={c}>
                  <Text type="h4">{categoryInfo.displayName}</Text>
                  {
                    categoryInfo.disclaimerText && <div className={styles.disclaimer}><Text type="caption-1">{categoryInfo.disclaimerText}</Text></div>
                  }
                  <Spacer size={8}/>
                  { options.map(o => renderOption(o, c, categoryInfo.multiSelectionAllowed)) }
                </div>
              )
            }
          })
        }

        <div className={styles.separator} style={{ display: turnkeyAvailable() ? 'block' : 'none' }}/>

        <div className={styles.total} style={{ display: turnkeyAvailable() ? 'flex' : 'none' }}>
          <div className={styles.totalTitle}>
            <Text type="h3">Total</Text>
            <Text type="body-short-1" color={PALETTE.gray50}>Excluding tax and shipping</Text>
          </div>
          <Text type="h3">{getTotal()}</Text>
        </div>

        <div className={styles.separator} style={{ display: turnkeyAvailable() ? 'block' : 'none' }}/>

        {turnkeyAvailable() && <ContactForm selectedOptionIds={getSelectedOptionsIds()}/>}
      </div>
    </div>
  )
}

export default TurnkeySolutions
